import React, { Component } from 'react';
import i18n from '../../../../i18n';
import { getAllAuctions } from '../../../../models/auctions';
import '../../../../styles/components/auction-map.scss';

const DEFAULT_CENTER = [23.5880, 58.3829];
const DEFAULT_ZOOM = 10;

// Pure vanilla countdown — no react-countdown dependency needed
function startCountdown(el, endDate) {
    const end = new Date(endDate).getTime();

    function pad(n) {
        return String(Math.max(0, n)).padStart(2, '0');
    }

    function renderTimer() {
        const now = Date.now();
        const diff = end - now;

        if (diff <= 0) {
            el.innerHTML = `<span class="am-timer__done">${i18n.t('ended') || 'Ended'}</span>`;
            return null;
        }

        const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        const units = [
            { val: days,    label: i18n.t('lbl_day')    || 'Day' },
            { val: hours,   label: i18n.t('lbl_hour')   || 'Hr'  },
            { val: minutes, label: i18n.t('lbl_minute') || 'Min' },
            { val: seconds, label: i18n.t('lbl_second') || 'Sec' },
        ];

        el.innerHTML = `
            <div class="am-timer">
                ${units.map(u => `
                    <div class="am-timer__unit">
                        <div class="am-timer__digits">
                            ${pad(u.val).split('').map(d => `<span class="am-timer__digit">${d}</span>`).join('')}
                        </div>
                        <span class="am-timer__label">${u.label}</span>
                    </div>
                `).join('')}
            </div>
        `;
    }

    renderTimer();
    const interval = setInterval(() => {
        if (!el || !document.body.contains(el)) {
            clearInterval(interval);
            return;
        }
        const done = renderTimer();
        if (done === null) clearInterval(interval);
    }, 1000);

    return interval;
}

class AuctionMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            auctions: [],
            loading: true,
        };
        this.mapRef = React.createRef();
        this.map = null;
        this.clusterGroup = null;
        this.timerIntervals = {};
    }

    componentDidMount() {
        this.initMap();
        this.loadAuctions();
    }

    componentWillUnmount() {
        Object.values(this.timerIntervals).forEach(clearInterval);
        this.timerIntervals = {};
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    initMap() {
        const L = window.L;
        if (!L) {
            console.error('Leaflet not loaded. Add leaflet CDN to index.html');
            return;
        }

        this.map = L.map(this.mapRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            zoomControl: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 18,
        }).addTo(this.map);

        L.control.zoom({ position: 'bottomright' }).addTo(this.map);

        this.clusterGroup = L.markerClusterGroup({
            showCoverageOnHover: false,
            maxClusterRadius: 60,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: (cluster) => {
                const count = cluster.getChildCount();
                let size = 'am-cluster--sm';
                if (count >= 50) size = 'am-cluster--lg';
                else if (count >= 10) size = 'am-cluster--md';

                return L.divIcon({
                    html: `<div class="am-cluster ${size}"><span>${count}</span></div>`,
                    className: 'am-cluster-wrapper',
                    iconSize: L.point(44, 44),
                });
            },
        });

        this.map.addLayer(this.clusterGroup);
    }

    async loadAuctions() {
        this.setState({ loading: true });
        try {
            const response = await getAllAuctions('limit=30');
            const allAuctions = response?.data || [];

            const withCoords = allAuctions.filter(
                (a) =>
                    a.latitude &&
                    a.longitude &&
                    !isNaN(parseFloat(a.latitude)) &&
                    !isNaN(parseFloat(a.longitude))
            );

            this.setState({ auctions: withCoords, loading: false }, () => {
                this.plotMarkers(withCoords);
            });
        } catch (e) {
            console.error('Failed to load auctions for map:', e);
            this.setState({ loading: false });
        }
    }

    plotMarkers(auctions) {
        const L = window.L;
        if (!L || !this.clusterGroup) return;

        this.clusterGroup.clearLayers();

        const current_lng = localStorage.getItem('I18N_LANGUAGE');

        auctions.forEach((auction) => {
            const lat = parseFloat(auction.latitude);
            const lng = parseFloat(auction.longitude);
            const title =
                current_lng === 'ar'
                    ? auction.title_ar || auction.title
                    : auction.title;
            const image = auction.images?.[0]?.image || auction.image || '';
            const isActive = auction.status === 'A';
            const endDate = auction.end_date;

            const locationText =
                current_lng === 'ar'
                    ? auction.location_ar || auction.location
                    : auction.location || auction.location_ar;

            const customIcon = L.divIcon({
                html: `<div class="am-pin"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
                className: 'am-pin-wrapper',
                iconSize: L.point(32, 32),
                iconAnchor: L.point(16, 32),
                popupAnchor: L.point(0, -34),
            });

            const popupContent = `
                <div class="am-popup">
                    ${image ? `<div class="am-popup__img-wrap"><img src="${image}" alt="" class="am-popup__img" /></div>` : ''}
                    <div class="am-popup__body">
                        <div class="am-popup__id">MZAD${auction.id}</div>
                        <div class="am-popup__title">${title || ''}</div>
                        ${isActive && endDate ? `<div class="am-popup__timer" id="am-timer-${auction.id}"></div>` : ''}
                        <div class="am-popup__meta">
                            ${locationText ? `
                                <span class="am-popup__loc">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                        <circle cx="12" cy="10" r="3"/>
                                    </svg>
                                    ${locationText}
                                </span>` : ''}
                            ${auction.current_amount ? `<span class="am-popup__price">${Number(auction.current_amount).toLocaleString()} OMR</span>` : ''}
                        </div>
                        <a href="/auction-details/${auction.id}" class="am-popup__btn">${i18n.t('view_details') || 'View Details'}</a>
                    </div>
                </div>
            `;

            const marker = L.marker([lat, lng], { icon: customIcon });
            marker.bindPopup(popupContent, {
                maxWidth: 280,
                minWidth: 220,
                className: 'am-popup-container',
            });

            if (isActive && endDate) {
                marker.on('popupopen', () => {
                    // Clear any existing interval for this auction
                    if (this.timerIntervals[auction.id]) {
                        clearInterval(this.timerIntervals[auction.id]);
                        delete this.timerIntervals[auction.id];
                    }

                    // Small timeout to ensure the popup DOM is fully rendered
                    setTimeout(() => {
                        const el = document.getElementById(`am-timer-${auction.id}`);
                        if (el) {
                            const interval = startCountdown(el, endDate);
                            if (interval) {
                                this.timerIntervals[auction.id] = interval;
                            }
                        }
                    }, 50);
                });

                marker.on('popupclose', () => {
                    if (this.timerIntervals[auction.id]) {
                        clearInterval(this.timerIntervals[auction.id]);
                        delete this.timerIntervals[auction.id];
                    }
                });
            }

            this.clusterGroup.addLayer(marker);
        });

        if (auctions.length > 0) {
            const bounds = this.clusterGroup.getBounds();
            if (bounds.isValid()) {
                this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
            }
        }
    }

    render() {
        const { loading, auctions } = this.state;

        return (
            <div className="am-page">
                <div className="am-map" ref={this.mapRef} />

                {loading && (
                    <div className="am-loading">
                        <div className="am-loading__spinner" />
                        <span>{i18n.t('loading_map') || 'Loading map...'}</span>
                    </div>
                )}

                {!loading && (
                    <div className="am-count-badge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                        </svg>
                        <span>{auctions.length} {i18n.t('auctions') || 'Auctions'}</span>
                    </div>
                )}
            </div>
        );
    }
}

export default AuctionMap;